// Track generation & playability validation (§2.6). Pure integer math.
//
// DUAL-PATH (§4bis.24): generation is keyed by the track's persisted
// sim_version. The '3' table below is FROZEN — every pre-v4 track and share
// link regenerates through those numbers byte-identically forever (pinned by
// test/fixtures/v3-tracks.json). Only the current version's row may ever
// change, and changing it is a SIM_VERSION bump.

import {
  BAR_AFTER_GAP_FRAMES,
  BAR_UNLOCK_FRAME,
  BAR_W,
  COMBO_CHANCE_PERCENT,
  COMBO_SPACING_FRAMES,
  EARLY_RELIEF_DEN,
  EARLY_RELIEF_FRAME,
  EARLY_RELIEF_NUM,
  GAP_UNLOCK_FRAME,
  GAP_W_MAX,
  GAP_W_MIN,
  HITBOX_STAND_W,
  INTERVAL_BASE,
  INTERVAL_DROP,
  JITTER_PERCENT,
  MAX_FRAMES,
  MAX_GAP_CROSS_FRAMES,
  MIN_SPACING_FRAMES,
  PLAYER_START_X,
  SIM_VERSION,
  SPEED_RAMP_FRAMES,
  SPIKE_W,
  TRACK_FIRST_FRAME,
  TRACK_LAST_FRAME,
} from './constants';
import { distanceAtFrame, frameAtTrackX, speedAtFrame } from './distance';
import { mulberry32, nextInt, type Rng } from './prng';
import type { GenVersion, Obstacle, ObstacleType, TrackValidation } from './types';

/** The generation parameters that differ between supported sim versions. */
export interface GenParams {
  /** Frame of the first obstacle. */
  firstFrame: number;
  /** Intervals before this frame get stretched by reliefNum/reliefDen. */
  reliefFrame: number;
  reliefNum: number;
  reliefDen: number;
  /** Gap type rolls before this frame are forced to 'spike'; 0 = gaps from #1. */
  gapUnlockFrame: number;
}

export const GEN_PARAMS: Record<GenVersion, GenParams> = {
  // sim v3 — FROZEN (do not touch: v3 replays depend on these numbers).
  '3': { firstFrame: 240, reliefFrame: 720, reliefNum: 7, reliefDen: 5, gapUnlockFrame: 0 },
  // sim v4 — current; the values live in constants.ts (§2.6).
  '4': {
    firstFrame: TRACK_FIRST_FRAME,
    reliefFrame: EARLY_RELIEF_FRAME,
    reliefNum: EARLY_RELIEF_NUM,
    reliefDen: EARLY_RELIEF_DEN,
    gapUnlockFrame: GAP_UNLOCK_FRAME,
  },
};
// Frozen at runtime, not just by convention — the '3' row is a replay contract.
for (const row of Object.values(GEN_PARAMS)) Object.freeze(row);
Object.freeze(GEN_PARAMS);

/**
 * Params for a persisted sim_version string. Unknown versions (e.g. a '2' row
 * predating the dual path) fall back to the current table — exactly the
 * pre-dual-path behavior for those rows.
 */
export function genParams(gen: string = SIM_VERSION): GenParams {
  return GEN_PARAMS[gen as GenVersion] ?? GEN_PARAMS[SIM_VERSION as GenVersion]!;
}

/** intervalFrames(f) = 210 − (144·min(f, 2700)/2700 | 0). 210 → 66 frames. */
export function intervalFrames(f: number): number {
  let c = f | 0;
  if (c < 0) c = 0;
  else if (c > SPEED_RAMP_FRAMES) c = SPEED_RAMP_FRAMES;
  return (INTERVAL_BASE - ((Math.imul(INTERVAL_DROP, c) / SPEED_RAMP_FRAMES) | 0)) | 0;
}

/** Ramp progress in permille: 0 at frame 0, 1000 from frame 2700 on. */
function progressPermille(f: number): number {
  let c = f | 0;
  if (c < 0) c = 0;
  else if (c > SPEED_RAMP_FRAMES) c = SPEED_RAMP_FRAMES;
  return (Math.imul(1000, c) / SPEED_RAMP_FRAMES) | 0;
}

function pickType(rng: Rng, f: number, p: GenParams): ObstacleType {
  // The roll is always consumed so the PRNG stream is version-independent up to
  // the type decision itself (§4bis.24: forcing happens on the result).
  const r = nextInt(rng, 100);
  if (f < BAR_UNLOCK_FRAME) {
    const t: ObstacleType = r < 60 ? 'spike' : 'gap';
    return t === 'gap' && f < p.gapUnlockFrame ? 'spike' : t;
  }
  if (r < 40) return 'spike';
  if (r < 70) return 'bar';
  return 'gap';
}

/**
 * Gap width for a gap whose leading edge is reached at frame f. Scaled with the
 * local speed so the number of airborne frames needed grows with difficulty,
 * then clamped into the spec's [2048, 3584] band.
 */
function gapWidth(rng: Rng, f: number): number {
  const speed = speedAtFrame(f);
  const crossFrames = (10 + ((Math.imul(12, progressPermille(f)) / 1000) | 0)) | 0; // 10 → 22
  let w = (HITBOX_STAND_W + Math.imul(speed, crossFrames)) | 0;
  // ±10% jitter, integer.
  const jitter = (Math.imul(w, nextInt(rng, 21) - 10) / 100) | 0;
  w = (w + jitter) | 0;
  if (w < GAP_W_MIN) w = GAP_W_MIN;
  if (w > GAP_W_MAX) w = GAP_W_MAX;
  // Never emit a gap the player provably cannot clear at this speed.
  const maxJumpable = (HITBOX_STAND_W + Math.imul(speed, MAX_GAP_CROSS_FRAMES)) | 0;
  if (w > maxJumpable) w = maxJumpable < GAP_W_MIN ? GAP_W_MIN : maxJumpable;
  return w | 0;
}

function widthOf(type: ObstacleType, rng: Rng, f: number): number {
  if (type === 'spike') return SPIKE_W;
  if (type === 'bar') return BAR_W;
  return gapWidth(rng, f);
}

/**
 * Deterministic track for `seed`. Obstacle x is absolute track x (FQ), so the
 * player's reference x reaches obstacle i exactly at its generation frame.
 */
export function generateTrack(seed: number, gen: string = SIM_VERSION): Obstacle[] {
  const p = genParams(gen);
  const rng = mulberry32(seed | 0);
  const out: Obstacle[] = [];

  let f = p.firstFrame;
  let lastFrame = -MIN_SPACING_FRAMES * 2;
  let lastGapEndFrame = -BAR_AFTER_GAP_FRAMES * 2;

  const place = (type: ObstacleType, frame: number): void => {
    const w = widthOf(type, rng, frame);
    const x = (PLAYER_START_X + distanceAtFrame(frame)) | 0;
    out.push({ type, x, w });
    lastFrame = frame;
    if (type === 'gap') lastGapEndFrame = frameAtTrackX((x + w) | 0);
  };

  while (f < TRACK_LAST_FRAME) {
    // §2.6.7 playability: keep separate action windows apart.
    const earliest = (lastFrame + MIN_SPACING_FRAMES) | 0;
    if (f < earliest) f = earliest;
    if (f >= TRACK_LAST_FRAME) break;

    let type = pickType(rng, f, p);
    // §2.6.7: no bar right after a gap — you cannot slide over a hole.
    if (type === 'bar' && f - lastGapEndFrame < BAR_AFTER_GAP_FRAMES) type = 'spike';

    const leadFrame = f;
    place(type, leadFrame);

    // §2.6.5 combos: a spike followed one airtime later by a spike or bar.
    if (
      type === 'spike' &&
      leadFrame >= BAR_UNLOCK_FRAME &&
      nextInt(rng, 100) < COMBO_CHANCE_PERCENT
    ) {
      const comboFrame = (leadFrame + COMBO_SPACING_FRAMES) | 0;
      if (comboFrame < TRACK_LAST_FRAME) {
        let comboType: ObstacleType = nextInt(rng, 2) === 0 ? 'spike' : 'bar';
        if (comboType === 'bar' && comboFrame - lastGapEndFrame < BAR_AFTER_GAP_FRAMES) {
          comboType = 'spike';
        }
        place(comboType, comboFrame);
      }
    }

    // §2.6.2-3: next interval from the lead obstacle, ±20% jitter.
    // Early-game relief: stretch intervals before the relief frame
    // (v3: ×1.4 before 12 s; v4: ×2.0 before 15 s).
    let iv = intervalFrames(leadFrame);
    if (leadFrame < p.reliefFrame) {
      iv = ((iv * p.reliefNum) / p.reliefDen) | 0;
    }
    const jitter = (Math.imul(iv, nextInt(rng, JITTER_PERCENT * 2 + 1) - JITTER_PERCENT) / 100) | 0;
    f = (leadFrame + iv + jitter) | 0;
  }

  return out;
}

/**
 * Playability check for the weekly track (§2.6). Regenerates from the seed so
 * server and client validate exactly the same bytes.
 */
export function validateTrack(seed: number, gen: string = SIM_VERSION): TrackValidation {
  const p = genParams(gen);
  const obstacles = generateTrack(seed, gen);
  const issues: string[] = [];

  if (obstacles.length < 12) {
    issues.push(`too few obstacles (${obstacles.length})`);
  }

  let lastGapEndFrame = -BAR_AFTER_GAP_FRAMES * 2;
  let prev: Obstacle | null = null;
  let prevFrame = 0;
  let tightRun = 0;

  for (let i = 0; i < obstacles.length; i++) {
    const o = obstacles[i]!;
    const f = frameAtTrackX(o.x);
    const speed = speedAtFrame(f);

    if (f < p.firstFrame) issues.push(`obstacle ${i}: frame ${f} before ${p.firstFrame}`);
    if (f >= MAX_FRAMES) issues.push(`obstacle ${i}: frame ${f} beyond MAX_FRAMES`);
    if (o.w <= 0) issues.push(`obstacle ${i}: non-positive width ${o.w}`);

    if (prev !== null) {
      if (o.x < prev.x + prev.w) issues.push(`obstacle ${i}: overlaps previous obstacle`);
      const spacing = f - prevFrame;
      if (spacing < COMBO_SPACING_FRAMES) {
        issues.push(`obstacle ${i}: only ${spacing} frames after previous (min ${COMBO_SPACING_FRAMES})`);
        tightRun++;
      } else if (spacing < MIN_SPACING_FRAMES) {
        // Allowed only as a deliberate combo: spike → spike|bar, pairs only.
        if (!(prev.type === 'spike' && (o.type === 'spike' || o.type === 'bar'))) {
          issues.push(`obstacle ${i}: illegal tight pair ${prev.type}→${o.type} (${spacing} frames)`);
        }
        if (tightRun >= 1) issues.push(`obstacle ${i}: combo chain longer than 2`);
        tightRun++;
      } else {
        tightRun = 0;
      }
    }

    if (o.type === 'bar') {
      if (f < BAR_UNLOCK_FRAME) issues.push(`obstacle ${i}: bar at frame ${f} before ${BAR_UNLOCK_FRAME}`);
      const since = f - lastGapEndFrame;
      if (since < BAR_AFTER_GAP_FRAMES) issues.push(`obstacle ${i}: bar only ${since} frames after a gap`);
      if (o.w !== BAR_W) issues.push(`obstacle ${i}: bar width ${o.w}`);
    } else if (o.type === 'spike') {
      if (o.w !== SPIKE_W) issues.push(`obstacle ${i}: spike width ${o.w}`);
    } else {
      if (f < p.gapUnlockFrame) issues.push(`obstacle ${i}: gap at frame ${f} before ${p.gapUnlockFrame}`);
      if (o.w < GAP_W_MIN || o.w > GAP_W_MAX) issues.push(`obstacle ${i}: gap width ${o.w} out of range`);
      // integer ceil((w - hitbox) / speed)
      const cross = (((o.w - HITBOX_STAND_W + speed - 1) / speed) | 0) | 0;
      if (cross > MAX_GAP_CROSS_FRAMES) {
        issues.push(`obstacle ${i}: gap needs ${cross} airborne frames (max ${MAX_GAP_CROSS_FRAMES})`);
      }
      lastGapEndFrame = frameAtTrackX((o.x + o.w) | 0);
    }

    prev = o;
    prevFrame = f;
  }

  return { ok: issues.length === 0, issues };
}
